import { useCollection } from '@cloudscape-design/collection-hooks';
import {
  Alert,
  Box,
  Button,
  Container,
  Header,
  Link,
  Pagination,
  SpaceBetween,
  StatusIndicator,
  Table,
} from '@cloudscape-design/components';
import { useQuery } from '@tanstack/react-query';

import type { RTFWebhookEvent } from '../../common/api/scorecardMonitor';
import { formatDateStringWithTime } from '../../common/formatting/dateFormat';

const failureLabels: Record<string, string> = {
  blocked: 'Collection blocked',
  failed: 'Delivery failed',
  rejected: 'Rejected',
  collection_failed: 'Collection failed · RTF notified',
  delayed: 'Completion delayed',
  lookup_failed: 'Client check failed · request allowed',
};

export function WebhookFailures({ loadEvents }: { loadEvents: () => Promise<RTFWebhookEvent[]> }) {
  const query = useQuery({
    queryKey: ['scorecard-monitor', 'webhooks'],
    queryFn: loadEvents,
    refetchInterval: 60_000,
  });
  const failures = (query.data ?? []).filter((event) => event.status in failureLabels);
  const { items, paginationProps, collectionProps } = useCollection(failures, {
    pagination: { pageSize: 10 },
  });

  return (
    <section aria-label="Partner webhook failures">
      <Container>
        <SpaceBetween size="m">
          {query.isError && (
            <Alert type="error">Could not load partner webhooks. Try refreshing.</Alert>
          )}
          <Table
            {...collectionProps}
            variant="embedded"
            header={
              <Header
                variant="h2"
                counter={`(${failures.length})`}
                actions={
                  <Button
                    iconName="refresh"
                    loading={query.isFetching}
                    onClick={() => void query.refetch()}
                  >
                    Refresh webhooks
                  </Button>
                }
              >
                Partner webhook failures
              </Header>
            }
            items={items}
            loading={query.isPending}
            loadingText="Loading partner webhooks"
            wrapLines
            columnDefinitions={[
              {
                id: 'webhook',
                header: 'Webhook',
                cell: (event) => (
                  <SpaceBetween size="xxs">
                    {event.business_name && event.report_uuid && (
                      <Link href={`/${event.report_uuid}`}>{event.business_name}</Link>
                    )}
                    <Box>
                      {event.direction === 'incoming' ? 'Incoming' : 'Outgoing'} · {event.operation}
                    </Box>
                    <Box color="text-body-secondary">
                      {formatDateStringWithTime(event.created_at)}
                    </Box>
                  </SpaceBetween>
                ),
              },
              {
                id: 'problem',
                header: 'Problem',
                cell: (event) => (
                  <SpaceBetween size="xxs">
                    <StatusIndicator
                      type={event.status === 'collection_failed' ? 'warning' : 'error'}
                    >
                      {failureLabels[event.status]}
                      {event.http_status ? ` · HTTP ${event.http_status}` : ''}
                    </StatusIndicator>
                    <Box>{event.summary}</Box>
                  </SpaceBetween>
                ),
              },
            ]}
            pagination={<Pagination {...paginationProps} />}
            empty={
              <Box textAlign="center" padding="m">
                {query.isError ? 'Webhook history is unavailable.' : 'No partner webhook failures.'}
              </Box>
            }
          />
        </SpaceBetween>
      </Container>
    </section>
  );
}
